import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Camera, Download, ArrowLeft, Star, Loader2, ExternalLink } from 'lucide-react';

export default function PhotoGalleryPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [reviewLink, setReviewLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const token = localStorage.getItem('rw_token');

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    bookingsAPI.getBookingPhotos(bookingId)
      .then(r => {
        setPhotos(r.data.photos || []);
        setReviewLink(r.data.google_review_link || '');
      })
      .catch(() => toast.error('Impossible de charger les photos'))
      .finally(() => setLoading(false));
  }, [bookingId, token, navigate]);

  const handleDownload = async (photo) => {
    try {
      const url = bookingsAPI.getPhotoUrl(photo.id);
      const resp = await fetch(url);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = photo.original_filename || 'riding-watt-photo.jpg';
      a.click();
      URL.revokeObjectURL(a.href);
      // Show Google review prompt after download
      if (reviewLink) {
        setShowReview(true);
      }
    } catch {
      toast.error('Erreur lors du telechargement');
    }
  };

  const handleDownloadAll = async () => {
    for (const p of photos) {
      await handleDownload(p);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-8" data-testid="photo-gallery-page">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00FF00]/10 flex items-center justify-center">
            <Camera className="w-8 h-8 text-[#00FF00]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Vos photos Riding Watt</h1>
          <p className="text-slate-400 text-sm">{photos.length} photo{photos.length > 1 ? 's' : ''} disponible{photos.length > 1 ? 's' : ''}</p>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-12 bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A]">
            <Camera className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Aucune photo disponible pour cette reservation.</p>
          </div>
        ) : (
          <>
            {/* Download all button */}
            <div className="flex justify-end mb-4">
              <Button onClick={handleDownloadAll} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-sm" data-testid="download-all-btn">
                <Download className="w-4 h-4 mr-2" /> Telecharger tout ({photos.length})
              </Button>
            </div>

            {/* Photo grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photos-grid">
              {photos.map(p => (
                <div key={p.id} className="group relative rounded-xl overflow-hidden bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#00FF00]/30 transition-colors cursor-pointer" onClick={() => setSelectedPhoto(p)} data-testid={`photo-${p.id}`}>
                  <img src={bookingsAPI.getPhotoUrl(p.id)} alt={p.original_filename} className="w-full h-40 sm:h-48 object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <button onClick={(e) => { e.stopPropagation(); handleDownload(p); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-3 rounded-full bg-[#00FF00] text-black" data-testid={`download-photo-${p.id}`}>
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Full-size photo viewer */}
        {selectedPhoto && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
            <img src={bookingsAPI.getPhotoUrl(selectedPhoto.id)} alt="" className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <button onClick={(e) => { e.stopPropagation(); handleDownload(selectedPhoto); }} className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl bg-[#00FF00] text-black font-bold flex items-center gap-2">
              <Download className="w-4 h-4" /> Telecharger
            </button>
          </div>
        )}

        {/* Google Review Prompt */}
        {showReview && reviewLink && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="google-review-prompt">
            <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-8 max-w-md w-full text-center animate-fadeInUp">
              <div className="flex justify-center mb-4 gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />)}
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Merci d'avoir choisi Riding Watt !</h2>
              <p className="text-slate-400 text-sm mb-6">Vos photos sont pretes ! Si vous avez apprecie votre experience, laissez-nous un avis Google. Cela nous aide enormement !</p>
              <div className="flex flex-col gap-3">
                <a href={reviewLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#00FF00] text-black font-bold hover:bg-[#00CC00] transition-colors" data-testid="google-review-btn">
                  <Star className="w-4 h-4" /> Laisser un avis Google
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button onClick={() => setShowReview(false)} className="px-6 py-3 rounded-xl text-slate-400 hover:text-white text-sm transition-colors" data-testid="skip-review-btn">
                  Peut-etre plus tard
                </button>
              </div>
            </div>
            <style>{`
              @keyframes fadeInUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
              .animate-fadeInUp { animation: fadeInUp 0.4s ease-out; }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
}
